/**
 * E-GTCC-VIEW-TEN1 — the reference building blocks this view needs on top of
 * the shared ones.
 *
 * The Shared UI Foundation Rule (AGENTS.md) applies: `.dot`, `.row`, `.fresh`,
 * `.mono`, "nicht messbar" and the panel heading already exist as
 * `views/cockpit/components/FleetPrimitives.tsx` and are re-used verbatim
 * rather than re-implemented. What lives here is only what neither the cockpit
 * nor the data plane owns: the `.panel` wrapper, the failure gate over
 * `TenResult` and the expandable row that frames an existing gtcc1 card as the
 * detail of a reference row.
 *
 * The Owner order names B-GTCC-UI1 as the future home of these blocks and asks
 * for local placeholders with the same props until it merges — that is exactly
 * what this module is, and it mirrors `views/dplane/components/DplanePanels.tsx`
 * prop for prop so the later move is a rename.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { Button, Card, ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import type { UseQueryResult } from "@tanstack/react-query";

import type {
  TenEntitlements,
  TenLaunchStatus,
  TenLifecycleStatus,
  TenResult,
} from "../../../api/ten.js";
import {
  FleetDot,
  PANEL_HEADING_STYLE,
  type FleetTone,
} from "../../cockpit/components/FleetPrimitives.js";
import {
  TABLE_STYLE as CONTROL_TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
} from "../../control/ControlPrimitives.js";

/** The six `.tab`s of the reference, in reference order. */
export const TEN_TAB_IDS = [
  "tenants",
  "t360",
  "provisioning",
  "members",
  "invitations",
  "roles",
] as const;
export type TenTabId = (typeof TEN_TAB_IDS)[number];

/** What every tab of this view receives from `TenView`. */
export interface TenTabProps {
  readonly direction: "ltr" | "rtl";
  /** The tenant the 360°/members tabs are bound to; `null` = none chosen yet. */
  readonly selectedTenantId: string | null;
  /** Select a tenant and jump to a tab — navigation only, never a write. */
  readonly onOpenTenant: (tenantId: string, tab: TenTabId) => void;
  /** Jump to a tab without changing the tenant. */
  readonly onOpenTab: (tab: TenTabId) => void;
}

/** Query policy of the whole view — identical to the data plane's. */
export const TEN_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

/**
 * `.grid-main` — the two-column body of the reference.
 *
 * E-GTCC-DPLANE-TEN-1ZU1 — das Verhaeltnis der Referenz ist `1.9fr / 1fr`
 * (`GTCC_REFERENCE_2026-09-01.html:86`, so auch im Rahmen-Stylesheet
 * `gtcc/ui/cockpit-ui.css:189-193`), nicht `2fr / 1fr`. Die rechte Spalte
 * traegt die Mono-Werte der Mitglieder- und Einladungs-Zeilen; die Breite,
 * die `2fr` ihr nimmt, ist genau die, in der diese Werte beschnitten werden.
 * Dieselbe Korrektur wie in `views/dplane/components/DplanePanels.tsx`.
 */
export const GRID_MAIN_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.9fr) minmax(0, 1fr)",
  gap: "var(--gt-sys-space-3)",
  alignItems: "start",
};

/** The column stack inside `.grid-main`. */
export const GRID_COLUMN_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-3)",
  minWidth: 0,
};

/** `.tabs` of the reference — `font-size: 12px`. */
export const TAB_BAR_STYLE: CSSProperties = { fontSize: "var(--gt-sys-font-size-xs)" };

/** `.sub` — the muted sentence under a head or under a table. */
export const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/** `.mono muted` — the grey monospace addition inside a panel head. */
export const HEADING_NOTE_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  color: "var(--gt-sys-text-muted)",
};

/*
 * `.panel > h2` of the reference carries its addition on the trailing edge of
 * the panel, not glued to the title (Referenz Zeile 684: "Tenants" links,
 * "Suche: Name/ID · [+ Neuen Tenant anlegen]" rechts am Rand). Vorher stand
 * beides direkt nebeneinander (Abnahme E-GTCC-ACCEPT1).
 */
const PANEL_HEAD_ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

const PANEL_HEAD_TRAIL_STYLE: CSSProperties = {
  marginInlineStart: "auto",
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
  flexWrap: "wrap",
};

/** `.mono` — a monospace value cell. */
export const MONO_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text)",
};

/** `.host` — the emphasised first cell of a row. */
export const HOST_STYLE: CSSProperties = {
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  color: "var(--gt-sys-text)",
};

/** The accent-coloured "360° oeffnen →" cell of the reference. */
export const ACCENT_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-accent)",
};

/* A-GTCC-ROWGRID1: Schrift, Kopf- und Zellenmasse kommen aus den kanonischen
   `ControlPrimitives` (Ursache 2 des Abgleichs A-GTCC-ACCEPT-ALL2). Lokal
   bleibt nur die Trennlinie unter jeder Zeile. */
export const TABLE_STYLE: CSSProperties = CONTROL_TABLE_STYLE;

const BOTTOM_RULE: CSSProperties = { borderBlockEnd: "1px solid var(--gt-sys-border)" };

export const TABLE_HEAD_STYLE: CSSProperties = { ...TH_STYLE, ...BOTTOM_RULE };

export const TABLE_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  ...BOTTOM_RULE,
  textAlign: "start",
};

/** `.tbl-wrap` — the horizontally scrollable table box. */
export const TABLE_WRAP_STYLE: CSSProperties = { overflowX: "auto", minWidth: 0 };

/**
 * Tone of a lifecycle status. The reference draws `active` green and `trial`
 * amber; the remaining states follow the same reading — a paused or suspended
 * tenant is a warning, a cancelled one a failure, an expired one simply over.
 * Nothing here is computed from a free-text status (Owner decision P8).
 */
const LIFECYCLE_TONES: Record<TenLifecycleStatus, FleetTone> = {
  trial: "warn",
  active: "ok",
  paused: "warn",
  suspended: "warn",
  cancelled: "crit",
  expired: "muted",
};

export function lifecycleTone(status: TenLifecycleStatus | undefined): FleetTone {
  return status === undefined ? "muted" : LIFECYCLE_TONES[status];
}

/*
 * E-GTCC-DPLANE-TEN-1ZU1 — die Referenz schreibt den Abo-Zustand dort, wo er
 * eine Messung ist, als die Marke der Server-Zustandsmaschine: `active`,
 * `trial (bis 15.09.)` in der Spalte STATUS (Zeile 687-690) und
 * `active · managed_hardware` in der 360°-Zeile "Identität + Lebenszyklus"
 * (Zeile 699). Das ist kein Uebersetzungsverzicht, sondern dieselbe Regel, der
 * die Nachbarzellen schon folgen: TYP zeigt `managed_hardware`, die Verben
 * zeigen `[pause]`. Ein deutsches Wort an dieser einen Stelle waere das
 * einzige, das der Leser nicht mehr auf den Zustand abbilden kann, den der
 * Server kennt.
 *
 * Uebersetzt bleibt alles, was die Marke einordnet: das Filtermenue der alten
 * Ansicht (`GtccTenantsView.tsx:799`) und der unmessbare Fall — fuer den hat
 * die Zustandsmaschine keine Marke, nur die Aussage, dass nichts gemessen
 * wurde.
 */
export function lifecycleTokenText(
  t: (key: string) => string,
  status: TenLifecycleStatus | null | undefined,
): string {
  if (status === null || status === undefined) return t("admin.gtcc.ten.lifecycle.unknown");
  return t(`admin.gtcc.ten.list.status.${status}`);
}

/*
 * E-GTCC-DPLANE-TEN-1ZU1 — die Mitglieder-Zeile der Referenz liest
 * `Owner · aktiv` (Zeile 730-732), nicht `owner · active`. Bisher gingen die
 * Rohwerte der Antwort ungefiltert in die Zeile; das ist ein durchgereichter
 * Aufzaehlungswert, kein Text fuer eine Oberflaeche. Uebersetzt wird beides
 * hier, an einer Stelle, weil zwei Tafeln dieselbe Zeile zeichnen (die
 * Uebersicht im Tab "Tenants" und der Tab "Mitglieder").
 *
 * Unbekannte Werte werden nicht geraten und nicht verschwiegen: sie stehen
 * so da, wie sie gemessen wurden (Owner-Entscheid P8).
 */
const MEMBER_ROLE_TAGS = ["owner", "admin", "dispatcher", "viewer"] as const;
const MEMBER_STATUS_TAGS = ["active", "invited", "pending", "suspended"] as const;

/*
 * Der Schluessel wird an der Aufrufstelle gebildet, nicht aus einem
 * Prefix-Argument: nur so bleibt er als Katalog-Schluessel sichtbar. Ein
 * `t(`${prefix}.${raw}`)` liest K-GTCC-Q14 als den Literal `prefix`-Text und
 * meldet die echten Schluessel deshalb als unbenutzt
 * (`gtcc-i18n-usage.test.tsx`).
 */
function labelFor(
  tags: ReadonlyArray<string>,
  raw: string | null | undefined,
  label: (tag: string) => string,
): string {
  if (raw === null || raw === undefined || raw === "") return "—";
  return tags.includes(raw) ? label(raw) : raw;
}

/** `Owner · aktiv` — Rolle und Zustand eines Mitglieds, wie die Referenz sie schreibt. */
export function memberValueText(
  t: (key: string) => string,
  role: string | null | undefined,
  status: string | null | undefined,
): string {
  const roleText = labelFor(MEMBER_ROLE_TAGS, role, (tag) =>
    tag === "owner"
      ? "Owner"
      : tag === "admin"
        ? "Admin"
        : tag === "dispatcher"
          ? "Dispatcher"
          : "Viewer",
  );
  const statusText = labelFor(MEMBER_STATUS_TAGS, status, (tag) =>
    t(`admin.gtcc.ten.member_status.${tag}`),
  );
  return `${roleText} · ${statusText}`;
}

/*
 * E-GTCC-DPLANE-TEN-1ZU1 — die beiden laengsten Zeilen der Tafel "TENANT 360°"
 * standen als Einwortwerte da, wo die Referenz die Messung ausschreibt
 * (`GTCC_REFERENCE_2026-09-01.html:697` und `:701`). "Handlungsbedarf" allein
 * nennt den Gesamtstand, aber nicht den Punkt, der ihn ausloest; "Hersteller:
 * alle" nennt eine von vier gemessenen Freigaben. Beide Antworten tragen die
 * fehlenden Angaben bereits (`TenLaunchStatus.items`,
 * `TenEntitlements.devices`) — sie wurden nur nicht gezeichnet.
 *
 * Geschrieben wird ausschliesslich Gemessenes. Ein Punkt ohne Messung wird
 * weggelassen, nicht geraten (Owner-Entscheid P8); die Haken der Referenz
 * stehen fuer "erlaubt/bereit", das Kreuz fuer "verweigert/blockiert", und ein
 * Punkt, der weder das eine noch das andere ist, traegt seinen gemessenen
 * Zustand im Klartext statt eines erfundenen Zeichens.
 */
const MARK_OK = "✓";
const MARK_NO = "✗";

/**
 * `Handlungsbedarf — Checkliste: Tenant ✓ · Erster Admin aktiv (Handlungsbedarf)`.
 * `null` heisst "nicht gemessen"; die Tafel setzt dann ihren eigenen Hinweis.
 */
export function launchValueText(
  t: (key: string, vars?: Record<string, string>) => string,
  launch: TenLaunchStatus | null | undefined,
): string | null {
  if (launch === null || launch === undefined || !launch.overall) return null;
  const head = t(`admin.gtcc.ten.launch.status.${launch.overall}`);
  const items = asList(launch.items).filter((item) => typeof item.id === "string");
  if (items.length === 0) return head;
  const checklist = items
    .map((item) => {
      const label = t(`admin.gtcc.ten.launch.item.${item.id}`);
      if (item.status === "ready") return `${label} ${MARK_OK}`;
      if (item.status === "blocked") return `${label} ${MARK_NO}`;
      return `${label} (${t(`admin.gtcc.ten.launch.status.${item.status}`)})`;
    })
    .join(" · ");
  return `${head} — ${t("admin.gtcc.ten.t360.launch_checklist", { items: checklist })}`;
}

/** `Selbstregistrierung: Freigabe erforderlich · IMEI änderbar ✓ · Gerät löschen ✗ · Hersteller: alle`. */
export function entitlementsValueText(
  t: (key: string, vars?: Record<string, string>) => string,
  entitlements: TenEntitlements | null | undefined,
): string | null {
  if (entitlements === null || entitlements === undefined) return null;
  const devices = entitlements.devices ?? {};
  const parts: string[] = [];

  if (typeof devices.self_register === "string" && devices.self_register !== "") {
    const value = SELF_REGISTER_VALUES.includes(devices.self_register)
      ? t(`admin.gtcc.ten.entitlements.value.${SELF_REGISTER_KEYS[devices.self_register]}`)
      : devices.self_register;
    parts.push(`${t("admin.gtcc.ten.entitlements.row.self_register")}: ${value}`);
  }
  for (const flag of ["imei_edit", "delete"] as const) {
    const raw = devices[flag];
    if (typeof raw !== "string" || raw === "") continue;
    const label = t(`admin.gtcc.ten.entitlements.row.${flag}`);
    if (raw === "allowed") parts.push(`${label} ${MARK_OK}`);
    else if (raw === "denied") parts.push(`${label} ${MARK_NO}`);
    else parts.push(`${label}: ${raw}`);
  }
  const vendors =
    entitlements.allowed_vendors === undefined || entitlements.allowed_vendors === null
      ? t("admin.gtcc.ten.t360.vendors_all")
      : String(entitlements.allowed_vendors.length);
  parts.push(t("admin.gtcc.ten.t360.entitlements_summary", { vendors }));

  return parts.join(" · ");
}

/** Die drei gemessenen Freigabewerte der Selbstregistrierung, mit ihrem Katalogschluessel. */
const SELF_REGISTER_KEYS: Record<string, string> = {
  allowed: "allowed",
  denied: "denied",
  approval: "approval_required",
};
const SELF_REGISTER_VALUES = Object.keys(SELF_REGISTER_KEYS);

/**
 * Eine Liste aus einer Antwort, die im Typ eine Liste ist, es zur Laufzeit aber
 * nicht sein muss (Owner-Entscheid P10: nie eine erfundene Zahl, aber auch nie
 * ein Absturz statt einer Aussage).
 */
export function asList<T>(value: ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
  return Array.isArray(value) ? value : [];
}

export function Mono(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId} style={MONO_STYLE}>
      {props.children}
    </span>
  );
}

/**
 * `.panel` — a card with an `h3` head. The reference writes `h2`; inside a GTCC
 * view the shell already owns the page `h1` and the view owns exactly one
 * level-1/2 heading (`gtcc-a11y-smoke.test.tsx:287`), so a panel head is an
 * `h3` carrying the reference's panel type scale.
 */
export function TenPanel(props: {
  title: string;
  note?: ReactNode;
  /**
   * The controls the reference draws on the trailing edge of the panel head.
   * Sibling of the `h3`, never inside it: a button inside a heading would land
   * in the heading's accessible name and break every search for the title.
   */
  headExtra?: ReactNode;
  testId: string;
  children: ReactNode;
}): ReactNode {
  /*
   * `section` mit `aria-labelledby` statt eines blanken `div`: jede Tafel
   * traegt eine eigene Ueberschrift, und die Bedienelemente in ihr (vor allem
   * die gleichlautende Wiederholen-Schaltflaeche der Fehlertafeln) sind nur
   * dann unterscheidbar, wenn die Tafel als benannter Bereich ausgezeichnet
   * ist.
   */
  const headingId = `${props.testId}-title`;
  return (
    <Card elevation={0}>
      <section aria-labelledby={headingId}>
        <Stack gap={2}>
          <div style={PANEL_HEAD_ROW_STYLE}>
            <h3 id={headingId} style={PANEL_HEADING_STYLE} data-testid={`${props.testId}-title`}>
              {props.title}
            </h3>
            {props.note === undefined && props.headExtra === undefined ? null : (
              <div style={PANEL_HEAD_TRAIL_STYLE} data-testid={`${props.testId}-head-trail`}>
                {props.note === undefined ? null : (
                  <span style={HEADING_NOTE_STYLE}>{props.note}</span>
                )}
                {props.headExtra}
              </div>
            )}
          </div>
          <div data-testid={props.testId}>{props.children}</div>
        </Stack>
      </section>
    </Card>
  );
}

const EXPAND_ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-2)",
  flexWrap: "wrap",
  paddingBlock: "var(--gt-sys-space-2)",
  borderBlockEnd: "1px solid var(--gt-sys-border)",
};

const EXPAND_BUTTON_STYLE: CSSProperties = {
  appearance: "none",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "var(--gt-sys-text)",
  cursor: "pointer",
  textAlign: "start",
};

/*
 * B-GTCC-VIEW-TEN-DZ1 — the reference's canonical `.row .mono` rule
 * (`cockpit-ui.css:479`) pushes the value to the row's end with
 * `margin-left: auto`; this local expand/collapse row never carried that
 * rule, so a long value sat flush after the label and broke across several
 * lines instead of sitting right-aligned on its own line, exactly as
 * `ABGLEICH.md` records for the "Umbruch in TENANT 360°" and the
 * "Handlungsbedarf" checklist row. The content of both rows already matches
 * the reference (`GTCC_REFERENCE_2026-09-01.html:697-698`); only the
 * alignment was missing.
 */
const EXPAND_VALUE_STYLE: CSSProperties = {
  ...MONO_STYLE,
  marginInlineStart: "auto",
  textAlign: "end",
};

const EXPAND_DETAIL_STYLE: CSSProperties = {
  paddingBlock: "var(--gt-sys-space-2)",
  paddingInlineStart: "var(--gt-sys-space-4)",
};

/**
 * One `.row` of the reference's Tenant 360° panel, with the gtcc1 card behind
 * it as expandable detail.
 *
 * The Owner order is explicit here: "jede Row klappt ihre Karte aus der alten
 * Konsole als Detail aus (bestehende Karten-Komponenten wiederverwenden, nur
 * in Referenz-Bausteinen gerahmt)". The row itself therefore renders only the
 * reference's dot, label and mono summary; every fact, every state and every
 * write in the detail belongs to the card that is passed in.
 *
 * Rows without a card (`children` omitted) stay plain text — a row that cannot
 * expand must not offer a control that does nothing.
 */
export function TenRow(props: {
  tone: FleetTone;
  label: string;
  value: ReactNode;
  testId: string;
  children?: ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const detailId = `${props.testId}-detail`;
  const hasDetail = props.children !== undefined;

  return (
    <div data-testid={`${props.testId}-row`}>
      <div style={EXPAND_ROW_STYLE}>
        <FleetDot tone={props.tone} />
        {hasDetail ? (
          <button
            type="button"
            style={EXPAND_BUTTON_STYLE}
            aria-expanded={open}
            aria-controls={detailId}
            onClick={() => setOpen((previous) => !previous)}
            data-testid={`${props.testId}-toggle`}
          >
            {/*
             * Die Beschriftung traegt ihren eigenen Haken: eine Zeile ohne
             * Detail hat keinen Knopf, und der Knopf traegt neben dem Namen
             * den Hinweis zum Auf- und Zuklappen. Beides zusammen liest sich
             * nur ueber `-label` als der Name, der in der Referenz steht.
             */}
            <span data-testid={`${props.testId}-label`}>{props.label}</span>
            <span style={HEADING_NOTE_STYLE}>
              {" "}
              {open ? t("admin.gtcc.ten.row.collapse") : t("admin.gtcc.ten.row.expand")}
            </span>
          </button>
        ) : (
          <span data-testid={`${props.testId}-label`}>{props.label}</span>
        )}
        <span style={EXPAND_VALUE_STYLE} data-testid={`${props.testId}-value`}>
          {props.value}
        </span>
      </div>
      {hasDetail ? (
        <div id={detailId} hidden={!open} style={EXPAND_DETAIL_STYLE}>
          {/*
           * Der Inhalt wird erst montiert, wenn die Zeile offen ist: die alten
           * Karten laden jede fuer sich, und elf gleichzeitig geoeffnete Karten
           * waeren elf Abfragen fuer etwas, das niemand sieht.
           */}
          {open ? props.children : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One gate for every read of this view. A pending query renders the loading
 * state, a failure renders a named, visible failure — never an empty panel and
 * never a placeholder number (Owner decision P10).
 *
 * `readTen()` classifies every expected failure into the envelope, so a
 * rejected query is the unexpected case. It is still a failure and is drawn as
 * one: a query that errored must never keep showing "wird gemessen", because a
 * stale loading label reads as "still measuring" when in truth nothing is being
 * measured any more.
 */
export function TenGate<T>(props: {
  query: UseQueryResult<TenResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;
  if (query.data === undefined) {
    if (query.isError)
      return (
        <TenFailure
          testId={props.testId}
          body={t("admin.gtcc.ten.failure.transport")}
          onRetry={() => void query.refetch()}
        />
      );
    return (
      <div data-testid={`${props.testId}-loading`}>
        <LoadingState label={t("admin.gtcc.ten.loading_panel")} />
      </div>
    );
  }
  if (query.data.ok) return props.render(query.data.data);
  const failure = query.data;
  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.ten.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.ten.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.ten.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.ten.failure.transport");
  /*
   * "disabled" und "forbidden" heilt kein erneuter Versuch — dort bleibt die
   * Meldung ohne Schaltflaeche. "upstream" und "transport_error" koennen
   * voruebergehend sein, darum bieten sie den erneuten Versuch an.
   */
  const retryable = failure.kind === "upstream" || failure.kind === "transport_error";
  return (
    <TenFailure
      testId={props.testId}
      body={body}
      onRetry={retryable ? () => void query.refetch() : undefined}
    />
  );
}

function TenFailure(props: {
  testId: string;
  body: string;
  /*
   * `exactOptionalPropertyTypes`: "disabled" und "forbidden" reichen hier
   * bewusst `undefined` durch (kein erneuter Versuch), darum gehoert
   * `undefined` in den Typ und nicht nur die Abwesenheit der Eigenschaft.
   */
  onRetry?: (() => void) | undefined;
}): ReactNode {
  const { t } = useLocale();
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState
        severity="warn"
        title={t("admin.gtcc.ten.failure.title")}
        body={props.body}
        action={
          props.onRetry === undefined ? undefined : (
            <Button variant="secondary" onClick={props.onRetry}>
              {t("common.retry")}
            </Button>
          )
        }
      />
    </div>
  );
}

/**
 * Der muted-mono-Satz der Referenz unter den Rollen — und ueberall dort, wo
 * eine Referenz-Aussage ehrlich ausgegraut gehoert.
 */
export function TenMutedNote(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <p
      data-testid={props.testId}
      style={{
        margin: 0,
        fontFamily: "var(--gt-sys-font-family-mono)",
        fontSize: "var(--gt-sys-font-size-xs)",
        color: "var(--gt-sys-text-muted)",
      }}
    >
      {props.children}
    </p>
  );
}
